import { useState, useCallback, useRef, useEffect } from 'react';

const CURRENCY_CONFIG = {
  INR: {
    min: 500000, max: 10000000000,
    format: (v) => {
      if (v >= 10000000) return `\u20b9${(v / 10000000).toFixed(v >= 100000000 ? 1 : 2)}Cr`;
      if (v >= 100000) return `\u20b9${(v / 100000).toFixed(v >= 10000000 ? 0 : 1)}L`;
      return `\u20b9${Math.round(v).toLocaleString('en-IN')}`;
    },
    parse: (str) => {
      const n = str.replace(/[^\d.]/g, '');
      const v = parseFloat(n);
      if (isNaN(v)) return null;
      const lower = str.toLowerCase();
      if (lower.includes('cr')) return v * 10000000;
      if (lower.includes('l')) return v * 100000;
      return v;
    },
    labels: ['\u20b95L', '\u20b950L', '\u20b95Cr', '\u20b950Cr', '\u20b9500Cr'],
    labelPositions: [500000, 5000000, 50000000, 500000000, 5000000000],
  },
  AED: {
    min: 100000, max: 500000000,
    format: (v) => {
      if (v >= 1000000000) return `AED ${(v / 1000000000).toFixed(1)}B`;
      if (v >= 1000000) return `AED ${(v / 1000000).toFixed(v >= 10000000 ? 1 : 2)}M`;
      if (v >= 1000) return `AED ${(v / 1000).toFixed(0)}K`;
      return `AED ${v.toFixed(0)}`;
    },
    parse: (str) => {
      const n = str.replace(/[^\d.]/g, '');
      const v = parseFloat(n);
      if (isNaN(v)) return null;
      const lower = str.toLowerCase();
      if (lower.includes('b')) return v * 1000000000;
      if (lower.includes('m')) return v * 1000000;
      if (lower.includes('k')) return v * 1000;
      return v;
    },
    labels: ['100K', '1M', '10M', '100M', '500M'],
    labelPositions: [100000, 1000000, 10000000, 100000000, 500000000],
  },
  USD: {
    min: 10000, max: 500000000,
    format: (v) => {
      if (v >= 1000000000) return `$${(v / 1000000000).toFixed(1)}B`;
      if (v >= 1000000) return `$${(v / 1000000).toFixed(v >= 10000000 ? 1 : 2)}M`;
      if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
      return `$${v.toFixed(0)}`;
    },
    parse: (str) => {
      const n = str.replace(/[^\d.]/g, '');
      const v = parseFloat(n);
      if (isNaN(v)) return null;
      const lower = str.toLowerCase();
      if (lower.includes('b')) return v * 1000000000;
      if (lower.includes('m')) return v * 1000000;
      if (lower.includes('k')) return v * 1000;
      return v;
    },
    labels: ['$10K', '$100K', '$1M', '$10M', '$500M'],
    labelPositions: [10000, 100000, 1000000, 10000000, 500000000],
  },
  GBP: {
    min: 10000, max: 500000000,
    format: (v) => {
      if (v >= 1000000000) return `\u00a3${(v / 1000000000).toFixed(1)}B`;
      if (v >= 1000000) return `\u00a3${(v / 1000000).toFixed(v >= 10000000 ? 1 : 2)}M`;
      if (v >= 1000) return `\u00a3${(v / 1000).toFixed(0)}K`;
      return `\u00a3${v.toFixed(0)}`;
    },
    parse: (str) => {
      const n = str.replace(/[^\d.]/g, '');
      const v = parseFloat(n);
      if (isNaN(v)) return null;
      const lower = str.toLowerCase();
      if (lower.includes('b')) return v * 1000000000;
      if (lower.includes('m')) return v * 1000000;
      if (lower.includes('k')) return v * 1000;
      return v;
    },
    labels: ['\u00a310K', '\u00a3100K', '\u00a31M', '\u00a310M', '\u00a3500M'],
    labelPositions: [10000, 100000, 1000000, 10000000, 500000000],
  },
};

const getCfg = (c) => CURRENCY_CONFIG[c] || CURRENCY_CONFIG.AED;

// Logarithmic scale: value ↔ percentage (0-1)
const valToFrac = (v, min, max) => {
  const logMin = Math.log(Math.max(min, 1));
  const logMax = Math.log(Math.max(max, 2));
  return Math.max(0, Math.min(1, (Math.log(Math.max(v, min)) - logMin) / (logMax - logMin)));
};
const fracToVal = (f, min, max) => {
  const logMin = Math.log(Math.max(min, 1));
  const logMax = Math.log(Math.max(max, 2));
  return Math.exp(logMin + f * (logMax - logMin));
};

const BudgetRangeSlider = ({ minValue, maxValue, currency = 'AED', onChange }) => {
  const cfg = getCfg(currency);
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [localMin, setLocalMin] = useState(minValue || cfg.min);
  const [localMax, setLocalMax] = useState(maxValue || cfg.max * 0.4);
  const [editingMin, setEditingMin] = useState(false);
  const [editingMax, setEditingMax] = useState(false);
  const [editMinText, setEditMinText] = useState('');
  const [editMaxText, setEditMaxText] = useState('');

  // Track previous currency to only reset on actual change, not initial mount
  const prevCurrencyRef = useRef(currency);
  useEffect(() => {
    if (prevCurrencyRef.current === currency) return;
    prevCurrencyRef.current = currency;
    const c = getCfg(currency);
    const newMin = c.min;
    const newMax = c.max * 0.4;
    setLocalMin(newMin);
    setLocalMax(newMax);
    onChange?.({ min: newMin, max: newMax });
  }, [currency]);

  const clamp = useCallback((v) => Math.max(cfg.min, Math.min(cfg.max, v)), [cfg.min, cfg.max]);

  const getFracFromEvent = useCallback((e) => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.max(0, Math.min(1, (x - rect.left) / rect.width));
  }, []);

  const handleDown = useCallback((type) => (e) => {
    e.preventDefault();
    setDragging(type);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const frac = getFracFromEvent(e);
      const raw = fracToVal(frac, cfg.min, cfg.max);
      const v = clamp(raw);
      if (dragging === 'min') {
        const n = Math.min(v, localMax * 0.95);
        setLocalMin(n);
        onChange?.({ min: n, max: localMax });
      } else {
        const n = Math.max(v, localMin * 1.05);
        setLocalMax(n);
        onChange?.({ min: localMin, max: n });
      }
    };
    const handleUp = () => setDragging(null);
    window.addEventListener('mousemove', handleMove, { passive: true });
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: true });
    window.addEventListener('touchend', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [dragging, localMin, localMax, getFracFromEvent, onChange, cfg.min, cfg.max, clamp]);

  const minPct = valToFrac(localMin, cfg.min, cfg.max) * 100;
  const maxPct = valToFrac(localMax, cfg.min, cfg.max) * 100;

  const commitMinEdit = () => {
    const parsed = cfg.parse(editMinText);
    if (parsed !== null && parsed >= cfg.min && parsed < localMax) {
      setLocalMin(clamp(parsed));
      onChange?.({ min: clamp(parsed), max: localMax });
    }
    setEditingMin(false);
  };
  const commitMaxEdit = () => {
    const parsed = cfg.parse(editMaxText);
    if (parsed !== null && parsed > localMin && parsed <= cfg.max) {
      setLocalMax(clamp(parsed));
      onChange?.({ min: localMin, max: clamp(parsed) });
    }
    setEditingMax(false);
  };

  return (
    <div className="space-y-5" data-testid="budget-range-slider">
      {/* Value display + manual inputs */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1.5 block">Minimum</label>
          {editingMin ? (
            <input
              autoFocus
              type="text"
              value={editMinText}
              onChange={(e) => setEditMinText(e.target.value)}
              onBlur={commitMinEdit}
              onKeyDown={(e) => e.key === 'Enter' && commitMinEdit()}
              className="w-full bg-black/40 border border-gold/50 rounded-lg px-3 py-2.5 text-gold font-semibold text-sm focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/30 transition-all"
              data-testid="budget-input-min"
            />
          ) : (
            <button
              onClick={() => { setEditingMin(true); setEditMinText(cfg.format(localMin)); }}
              className="w-full text-left bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-gold font-semibold text-sm hover:border-gold/40 transition-all cursor-text"
              data-testid="budget-input-min"
            >
              {cfg.format(localMin)}
            </button>
          )}
        </div>
        <div className="text-white/15 mt-5 text-lg font-light select-none">\u2014</div>
        <div className="flex-1">
          <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1.5 block">Maximum</label>
          {editingMax ? (
            <input
              autoFocus
              type="text"
              value={editMaxText}
              onChange={(e) => setEditMaxText(e.target.value)}
              onBlur={commitMaxEdit}
              onKeyDown={(e) => e.key === 'Enter' && commitMaxEdit()}
              className="w-full bg-black/40 border border-gold/50 rounded-lg px-3 py-2.5 text-gold font-semibold text-sm focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/30 transition-all"
              data-testid="budget-input-max"
            />
          ) : (
            <button
              onClick={() => { setEditingMax(true); setEditMaxText(cfg.format(localMax)); }}
              className="w-full text-left bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-gold font-semibold text-sm hover:border-gold/40 transition-all cursor-text"
              data-testid="budget-input-max"
            >
              {cfg.format(localMax)}
            </button>
          )}
        </div>
      </div>

      {/* Continuous slider track */}
      <div className="px-1">
        <div
          ref={trackRef}
          className="relative h-2 rounded-full bg-white/[0.06] cursor-pointer select-none touch-none"
          data-testid="budget-slider-track"
        >
          {/* Active range fill with gradient */}
          <div
            className="absolute top-0 bottom-0 rounded-full"
            style={{
              left: `${minPct}%`,
              width: `${Math.max(maxPct - minPct, 0.5)}%`,
              background: 'linear-gradient(90deg, #C6A15B, #D4AF37, #E8C547)',
            }}
          />

          {/* Min thumb */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 z-20 transition-transform duration-100 ${dragging === 'min' ? 'scale-[1.3]' : 'hover:scale-[1.15]'}`}
            style={{ left: `${minPct}%`, marginLeft: '-10px' }}
            onMouseDown={handleDown('min')}
            onTouchStart={handleDown('min')}
            data-testid="budget-slider-min-thumb"
          >
            <div className={`w-5 h-5 rounded-full border-[2.5px] border-[#C6A15B] bg-[#0D0D0D] shadow-[0_0_12px_rgba(198,161,91,0.3)] cursor-grab ${dragging === 'min' ? 'ring-[3px] ring-[#C6A15B]/20' : ''}`} />
          </div>

          {/* Max thumb */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 z-20 transition-transform duration-100 ${dragging === 'max' ? 'scale-[1.3]' : 'hover:scale-[1.15]'}`}
            style={{ left: `${maxPct}%`, marginLeft: '-10px' }}
            onMouseDown={handleDown('max')}
            onTouchStart={handleDown('max')}
            data-testid="budget-slider-max-thumb"
          >
            <div className={`w-5 h-5 rounded-full border-[2.5px] border-[#C6A15B] bg-[#0D0D0D] shadow-[0_0_12px_rgba(198,161,91,0.3)] cursor-grab ${dragging === 'max' ? 'ring-[3px] ring-[#C6A15B]/20' : ''}`} />
          </div>
        </div>

        {/* Scale labels */}
        <div className="flex justify-between mt-2.5">
          {cfg.labels.map((l, i) => (
            <span key={i} className="text-[10px] text-white/20 font-medium">{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BudgetRangeSlider;
